import axios from 'axios';

// Prefer an explicit VITE_API_BASE_URL in environment. 
// HARDCODED FIX: If env is localhost (stale) or missing, use production
const ENV_URL = import.meta.env.VITE_API_BASE_URL;
const PROD_URL = 'https://wealth-plus-backend-786760620153.asia-south1.run.app';
const BASE_URL = import.meta.env.VITE_API_BASE_URL || PROD_URL;
// const BASE_URL = 'http://localhost:5000'; // FORCED LOCALHOST

export const API_BASE_URL = BASE_URL ? `${BASE_URL}/api` : '/api';

/**
 * Helper to get the full URL for an image.
 * If the URL is already a full URL (starts with http), it's returned as is.
 * Otherwise, it's prepended with the backend base URL.
 */
export const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;

  // For backend-relative paths, ensure /uploads/ prefix if missing and it's a known folder
  let targetUrl = url;
  if (!url.startsWith('/') && !url.startsWith('uploads/')) {
    // If it starts with posters/, profile_photos/, or profiles/, prepend uploads/
    if (url.startsWith('posters/') || url.startsWith('profile_photos/') || url.startsWith('profiles/')) {
      targetUrl = `uploads/${url}`;
    }
  }

  // Remove /api from the end of API_BASE_URL to get the backend root
  const backendRoot = API_BASE_URL.replace(/\/api$/, '');
  return `${backendRoot}${targetUrl.startsWith('/') ? '' : '/'}${targetUrl}`;
};

// Helper function to get cookie value
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getCookie('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Try to refresh token
      const refreshToken = getCookie('refreshToken');
      if (refreshToken) {
        try {
          const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken
          });

          if (refreshResponse.data.ok) {
            // Store new access token
            document.cookie = `token=${refreshResponse.data.data.accessToken}; path=/; max-age=86400`;

            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.data.accessToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Refresh failed, clear tokens
          document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
          document.cookie = 'refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        }
      }

      return Promise.resolve({ unauthorized: true });
    }
    return Promise.reject(error);
  }
);

export const getCommissionSettings = async () => {
  return await api.get('/commissions/settings');
};

export const saveCommissionSettings = async (settings) => {
  return await api.post('/commissions/settings', settings);
};

export const recalculateCommissionsForPeriod = async (periodData, settings) => {
  // Handle both old format (single period string) and new format (object with startPeriod, endPeriod, months)
  if (typeof periodData === 'string') {
    // Legacy format
    return await api.post('/commissions/recalculate', { period: periodData, settings });
  } else {
    // New format with time interval
    return await api.post('/commissions/recalculate', {
      startPeriod: periodData.startPeriod,
      endPeriod: periodData.endPeriod,
      months: periodData.months,
      settings
    });
  }
};

export const getAllPeopleWithRevenue = async (month = null) => {
  const params = month ? { month } : {};
  return await api.get('/dashboard/people', { params });
};

export const getSuperAdminDashboardData = async (params = {}) => {
  return await api.get('/dashboard/super-admin', { params });
};

export const getMonthlyPolicyPremiums = async (year) => {
  const params = year ? { year } : {};
  return await api.get('/dashboard/monthly-policy-premiums', { params });
};

export const getLeaderDashboardData = async (leaderId, month = null, year = null) => {
  const params = {};
  if (month) { params.month = month; }
  if (year) { params.year = year; }
  return await api.get(`/users/${leaderId}/leader-dashboard`, { params });
};

export const getAdvisorDashboardData = async (advisorId, month = null, year = null) => {
  const params = {};
  if (month) params.month = month;
  if (year) params.year = year;
  return await api.get(`/users/${advisorId}/advisor-dashboard`, { params });
};

export const approvePayout = async (commissionId, transactionData = null, approveOnly = false) => {
  const payload = transactionData || {};
  // Ensure the backend receives the approve_only flag (though /pay might ignore it, it expects transaction_ref)
  payload.approve_only = !!approveOnly;

  try {
    // Redirecting to /pay as /approve is deprecated (410)
    const result = await api.post(`/payouts/${commissionId}/pay`, payload);
    return result;
  } catch (error) {
    throw error;
  }
};

export const processCommissionPayment = async (commissionId, transactionData) => {
  // Redirecting to /payouts/:id/pay as well since that is the robust endpoint handling IDs
  return await api.post(`/payouts/${commissionId}/pay`, transactionData);
};

export const updateCommission = async (commissionId, newAmount) => {
  return await api.put(`/commissions/${commissionId}`, { totalCommission: newAmount });
};

export const getUsers = async (params = {}) => {
  const response = await api.get('/users', { params });
  const result = response.data || response;
  // Handle nested data structure from backend
  if (result.data && result.data.users) {
    return result.data.users;
  }
  return result.users || result || [];
};

export const createUser = async (userData) => {
  const isFormData = userData instanceof FormData;
  const response = await api.post('/users', userData, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
  });
  return response.data || response;
};

export const updateUser = async (userId, userData) => {
  const isFormData = userData instanceof FormData;
  const response = await api.put(`/users/${userId}`, userData, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
  });
  return response.data || response;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/users/${userId}`);
  return response.data || response;
};

export const uploadPoliciesExcel = async (file) => {
  const formData = new FormData();
  formData.append('excel', file);

  const response = await api.post('/policies/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data || response;
};

export const createManualPolicy = async (policyData) => {
  const response = await api.post('/policies/manual', policyData);
  return response.data || response;
};

export const getPolicies = async (params = {}) => {
  const response = await api.get('/policies', { params });
  const result = response.data || response;
  return result;
};

export const deletePolicy = async (policyId) => {
  const response = await api.delete(`/policies/${policyId}`);
  const result = response.data || response;
  return result;
};

export const updatePolicy = async (policyId, updateData) => {
  const response = await api.put(`/policies/${policyId}`, updateData);
  const result = response; // Return the wrapped response {ok: true, data: policy, message: ...}
  return result;
};

export const login = async (identifier, password) => {
  try {
    const response = await api.post('/auth/login', { identifier, password });
    const data = response;
    if (data.ok) {
      // Store tokens in cookies
      document.cookie = `token=${data.data.accessToken}; path=/; max-age=86400`; // 1 day
      document.cookie = `refreshToken=${data.data.refreshToken}; path=/; max-age=604800`; // 7 days
    }
    return data;
  } catch (error) {
    return error.response?.data || { ok: false, error: 'Login failed' };
  }
};

export const register = async (userData) => {
  try {
    const response = await api.post('/auth/register', userData);
    const data = response;
    if (data.ok) {
      // Store tokens in cookies
      document.cookie = `token=${data.data.accessToken}; path=/; max-age=86400`; // 1 day
      document.cookie = `refreshToken=${data.data.refreshToken}; path=/; max-age=604800`; // 7 days
    }
    return data;
  } catch (error) {
    return error.response?.data || { ok: false, error: 'Registration failed' };
  }
};

export const sendCommissionNotification = async (commissionId) => {
  try {
    const result = await api.post(`/commissions/${commissionId}/send-notification`);
    return result;
  } catch (error) {
    throw error;
  }
};

export const getCommissionCalculation = async (commissionId) => {
  return await api.get(`/commissions/${commissionId}/calculation`);
};

export const getPayouts = async (params = {}) => {
  const response = await api.get('/payouts', { params });
  // Handle the nested response structure from backend
  // Backend returns: { ok: true, data: { payouts: [...], pagination: {...} } }
  // We need to return the full response structure for the frontend to handle
  return response;
};

export const deletePayout = async (payoutId) => {
  try {
    const response = await api.delete(`/payouts/${payoutId}`);
    // The response interceptor returns response.data, so `response` here is already the payload
    if (response && response.unauthorized) {
      return { ok: false, error: 'Unauthorized' };
    }
    // If backend uses { ok: true, data: ... } shape, pass it through
    return response;
  } catch (error) {
    // Normalize error shape so UI can display a helpful message
    const data = error.response?.data;
    const message = data?.message || data?.error || error.message || 'Unknown error';
    return { ok: false, error: message };
  }
};

export const getPoliciesPreview = async (startDate, endDate) => {
  return await api.get('/commissions/policies/preview', {
    params: { startDate, endDate }
  });
};

// General Insurance APIs
export const createGeneralInsurance = async (formData) => {
  const response = await api.post('/general-insurance', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

export const getGeneralInsuranceList = async (params = {}) => {
  const response = await api.get('/general-insurance', { params });
  return response;
};

export const getGeneralInsuranceById = async (id) => {
  const response = await api.get(`/general-insurance/${id}`);
  return response;
};

export const approveGeneralInsurance = async (id, formData) => {
  const response = await api.put(`/general-insurance/${id}/approve`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

export const rejectGeneralInsurance = async (id, rejectionComment) => {
  const response = await api.put(`/general-insurance/${id}/reject`, {
    rejection_comment: rejectionComment
  });
  return response;
};

export const rollbackGeneralInsurance = async (id, rollbackReason) => {
  const response = await api.put('/general-insurance/' + id + '/rollback', {
    rollback_reason: rollbackReason
  });
  return response;
};

export const editApprovedGeneralInsurance = async (id, editData) => {
  const response = await api.put(`/general-insurance/${id}/edit-approved`, editData);
  return response;
};

export const editPendingGeneralInsurance = async (id, editData) => {
  const response = await api.put(`/general-insurance/${id}/edit`, editData);
  return response;
};

// GI Payouts APIs
export const getGIPayouts = async (params = {}) => {
  const response = await api.get('/general-insurance/payouts', { params });
  return response;
};

export const markGIPayoutPaid = async (payoutId, paymentData) => {
  const response = await api.put(`/general-insurance/payouts/${payoutId}/mark-paid`, paymentData);
  return response;
};

export const getGIMonthlySummary = async (month) => {
  const response = await api.get('/general-insurance/monthly-summary', { params: { month } });
  return response;
};

export const recalculateGIMonth = async (month) => {
  const response = await api.post('/general-insurance/recalculate-month', { month });
  return response;
};

export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/auth/forgot-password', { email });
    return response;
  } catch (error) {
    return error.response?.data || { ok: false, error: 'Failed to send reset code' };
  }
};

export const verifyToken = async (email, token) => {
  try {
    const response = await api.post('/auth/verify-token', { email, token });
    return response;
  } catch (error) {
    return error.response?.data || { ok: false, error: 'Failed to verify code' };
  }
};

export const resetPassword = async (email, token, newPassword) => {
  try {
    const response = await api.post('/auth/reset-password', {
      email,
      token,
      newPassword
    });
    return response;
  } catch (error) {
    return error.response?.data || { ok: false, error: 'Failed to reset password' };
  }
};

export const getCurrentUser = async () => {
  const token = getCookie('token');
  if (!token) {
    return null;
  }
  const result = await api.get('/auth/me');
  if (result.unauthorized) {
    return null;
  }
  return result.data;
};

// Poster management API functions
export const getPosters = async (params = {}) => {
  return await api.get('/posters', { params });
};

export const uploadPoster = async (formData) => {
  return await api.post('/posters/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const updatePoster = async (posterId, updateData) => {
  return await api.put(`/posters/${posterId}`, updateData);
};

export const deletePoster = async (posterId) => {
  return await api.delete(`/posters/${posterId}`);
};

export const getPosterCategories = async () => {
  return await api.get('/posters/categories');
};

export const createPosterCategory = async (categoryData) => {
  return await api.post('/posters/categories', categoryData);
};

export const updatePosterCategory = async (categoryId, updateData) => {
  return await api.put(`/posters/categories/${categoryId}`, updateData);
};

export const deletePosterCategory = async (categoryId) => {
  return await api.delete(`/posters/categories/${categoryId}`);
};



// Mutual Funds APIs
export const createMutualFund = async (formData) => {
  return await api.post('/mutual-funds', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getMutualFunds = async (params = {}) => {
  return await api.get('/mutual-funds', { params });
};

export const getMutualFundPayouts = async (params = {}) => {
  return await api.get('/mutual-funds/payouts', { params });
};

export const payMutualFundPayout = async (payoutId, paymentData) => {
  return await api.put(`/mutual-funds/payouts/${payoutId}/pay`, paymentData);
};

export const deleteMutualFundPayout = async (payoutId) => {
  return await api.delete(`/mutual-funds/payouts/${payoutId}`);
};

export const calculateMutualFundLeaderPayouts = async (data) => {
  return await api.post('/mutual-funds/calculate-leader-payouts', data);
};

export const updateMutualFund = async (id, data) => {
  return await api.put(`/mutual-funds/${id}`, data);
};

export const deleteMutualFund = async (id) => {
  return await api.delete(`/mutual-funds/${id}`);
};

export const getMutualFundDashboardData = async (params = {}) => {
  return await api.get('/mutual-funds/dashboard', { params });
};

export const getGeneralInsuranceDashboardData = async (params = {}) => {
  return await api.get('/general-insurance/dashboard', { params });
};

// Health Insurance APIs
export const createHealthInsurance = async (formData) => {
  const response = await api.post('/health-insurance', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

export const updateHealthInsurance = async (id, formData) => {
  const isFd = formData instanceof FormData;
  const response = await api.put(`/health-insurance/${id}`, formData, {
    headers: isFd ? { 'Content-Type': 'multipart/form-data' } : {}
  });
  return response;
};

export const deleteHealthInsurance = async (id) => {
  const response = await api.delete(`/health-insurance/${id}`);
  return response;
};

export const deleteHealthInsurancePayout = async (payoutId) => {
  try {
    const response = await api.delete(`/health-insurance/payouts/${payoutId}`);
    return response.data || response; // Handle both axios auto data unwrap or manual
  } catch (error) {
    return error.response?.data || { ok: false, error: 'Failed to delete payout' };
  }
};

export const getHealthInsuranceList = async (params = {}) => {
  return await api.get('/health-insurance', { params });
};

export const checkHealthInsuranceExpiry = async () => {
  return await api.post('/health-insurance/check-expiry');
};

export const getHealthInsurancePayouts = async (params = {}) => {
  return await api.get('/health-insurance/payouts', { params });
};

export const markHealthInsurancePayoutPaid = async (id, data) => {
  return await api.put(`/health-insurance/payouts/${id}/mark-paid`, data);
};

export const getHealthInsuranceMonthlySummary = async (month) => {
  return await api.get('/health-insurance/monthly-summary', { params: { month } });
};

export const recalculateHealthInsuranceMonth = async (month) => {
  return await api.post('/health-insurance/recalculate-month', { month });
};

// Health Insurance Renewal APIs
export const getExpiringHealthInsurance = async (days = 30) => {
  return await api.get('/health-insurance/expiring', { params: { days } });
};

export const requestHealthInsuranceRenewal = async (policyId, note = '') => {
  return await api.post(`/health-insurance/request-renewal/${policyId}`, { note });
};

export const cancelHealthInsuranceRenewal = async (policyId) => {
  return await api.post(`/health-insurance/cancel-renewal/${policyId}`);
};

export const getPendingHealthInsuranceRenewals = async () => {
  return await api.get('/health-insurance/pending-renewals');
};

export const approveHealthInsuranceRenewal = async (policyId, renewalData) => {
  const isFormData = renewalData instanceof FormData;
  return await api.post(`/health-insurance/approve-renewal/${policyId}`, renewalData, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
  });
};